import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BackspaceIcon from '@mui/icons-material/Backspace';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import axios from 'axios';
import { useKioskContext } from '../context/KioskContext';
import { useParams, useLocation } from 'react-router-dom';

const OrderNumber = ({ onBack, onOrderFound }) => {
  const { translate } = useKioskContext();
  const [orderNumber, setOrderNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { posId } = useParams();
  const location = useLocation();

  // Get posId from URL or use default
  const getPosId = () => {
    // Extract posId from URL if present
    let id = posId;
    
    // Apply alias mapping
    if (posId === "test") {
      id = "672312ec5b53fbbe37d4b0d5";
    } else if (posId === "khiang") {
      id = "67d3a2144a266ea37ce7ac69";
    } else if (posId === "khiangmpob") {
      id="680e35b78a5a51ec452b6678"
    }
    
    return id;
  };

  // Handle number pad button click
  const handleNumberClick = (num) => {
    if (orderNumber.length < 10) { // Limit order number length
      setOrderNumber(prev => prev + num);
    }
  };

  // Handle backspace button click
  const handleBackspace = () => {
    setOrderNumber(prev => prev.slice(0, -1));
  };

  // Handle clear button click
  const handleClear = () => {
    setOrderNumber('');
  };

  // Fetch order details
  const handleFetchOrder = async () => {
    if (!orderNumber) {
      setError(translate('Please enter an order number', {
        en: 'Please enter an order number',
        zh: '请输入订单号',
        th: 'กรุณาใส่หมายเลขคำสั่งซื้อ',
        tl: 'Mangyaring maglagay ng numero ng order'
      }));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const id = getPosId();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/get-order/${orderNumber}/${id}`
      );

      if (response?.data) {
        const orderData = response.data;

        const finalOrder = {
          ...orderData,
          receiptNumber: orderData.receiptNumber || null,
        };

        onOrderFound(finalOrder, finalOrder.receiptNumber);
      } else {
        setError(translate('Order not found', {
          en: 'Order not found',
          zh: '找不到订单',
          th: 'ไม่พบคำสั่งซื้อ',
          tl: 'Hindi nahanap ang order'
        }));
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      const serverMessage = error.response?.data?.message || '';
    
      if (serverMessage.includes("Order already processed")) {
        setError(serverMessage); 
      } else {
        setError(translate('Error retrieving order. Please try again.', {
          en: 'Error retrieving order. Please try again.',
          zh: '检索订单时出错。请再试一次。',
          th: 'เกิดข้อผิดพลาดในการดึงข้อมูลคำสั่งซื้อ กรุณาลองอีกครั้ง',
          tl: 'Error sa pagkuha ng order. Pakisubukang muli.'
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Generate number pad buttons
  const renderNumberPad = () => {
    const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
    return (
      <Grid container spacing={2}>
        {numbers.map((num) => (
          <Grid item xs={4} key={num}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => handleNumberClick(num)}
              sx={{
                py: 2,
                fontSize: '1.5rem',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              {num}
            </Button>
          </Grid>
        ))}
        <Grid item xs={4}>
          <Button
            variant="contained"
            color="error"
            fullWidth
            onClick={handleClear}
            sx={{
              py: 2,
              fontSize: '1.2rem',
            }}
          >
            {translate('Clear', {
              en: 'Clear',
              zh: '清除',
              th: 'ล้าง',
              tl: 'I-clear'
            })}
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={handleBackspace}
            sx={{
              py: 2,
              fontSize: '1.2rem',
            }}
          >
            <BackspaceIcon />
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={handleFetchOrder}
            disabled={loading || orderNumber.length === 0}
            sx={{
              py: 2,
              fontSize: '1.2rem',
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : <SearchIcon />}
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 3,
        backgroundColor: '#f5f5f5',
      }}
    >
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={onBack} 
          color="primary"
          sx={{ mr: 2, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.default' } }}
        >
          <ArrowBackIcon fontSize="large" />
        </IconButton>
        
        <Typography variant="h4" component="h1">
          {translate('Enter Order Number', {
            en: 'Enter Order Number',
            zh: '输入订单号',
            th: 'ใส่หมายเลขคำสั่งซื้อ',
            tl: 'Ilagay ang Numero ng Order'
          })}
        </Typography>
      </Box>

      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 3,
            }}
          >
            {/* Order number display */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                textAlign: 'center',
                mb: 3,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                backgroundColor: error ? '#fff4f4' : '#f9f9f9',
                borderColor: error ? 'error.main' : 'inherit',
              }}
            >
              <Typography variant="h2" color={error ? 'error' : 'inherit'}>
                {orderNumber || '_______'}
              </Typography>
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Number pad */}
            {renderNumberPad()}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderNumber;
